import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Container, Graphics } from '@pixi/react';
import Rectangle from './Rectangle';
import EditableLine from './EditableLine';

const MergedStructure = ({ 
  structure, 
  isSelected, 
  onUpdate, 
  onSelect, 
  stageRef,
  onUnmerge,
  selectedMeasurementUnit = {
    id: 'meters',
    name: 'Meters',
    symbol: 'm',
    abbreviation: 'm',
    conversionFactor: 1,
  },
  isBlueprintMode = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [initialStructure, setInitialStructure] = useState(null);
  const [draggedShapeId, setDraggedShapeId] = useState(null);
  const [selectedShapeId, setSelectedShapeId] = useState(null); // Track individual shape selection
  const [hiddenLinesMap, setHiddenLinesMap] = useState({}); // Track which lines to hide per rectangle
  const [connectionLines, setConnectionLines] = useState([]); // Track connection lines between rectangles
  const [deletedGapFillers, setDeletedGapFillers] = useState(new Set()); // Track explicitly deleted gap-fillers
  const [deletedSnappedLines, setDeletedSnappedLines] = useState(new Set()); // Track explicitly deleted snapped side lines
  const [structurePattern, setStructurePattern] = useState(null); // Track flooring pattern for the entire structure
  const containerRef = useRef();

  // Calculate bounding box of all shapes in the structure
  const getBoundingBox = useCallback((shapes) => {
    if (!shapes || shapes.length === 0) return { x: 0, y: 0, width: 0, height: 0 };

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    shapes.forEach(shape => {
      const left = shape.x;
      const top = shape.y;
      const right = shape.x + (shape.width || 0);
      const bottom = shape.y + (shape.height || 0);

      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);
    });

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }, []);

  // Get adjacency information between two rectangles
  const getAdjacencyInfo = useCallback((rectA, rectB, tolerance) => {
    const adjacency = {
      isAdjacent: false,
      side: null,
      sharedArea: null
    };

    // Check horizontal adjacency (left-right)
    const isHorizontallyAdjacent = 
      Math.abs(rectA.x + rectA.width - rectB.x) <= tolerance || 
      Math.abs(rectB.x + rectB.width - rectA.x) <= tolerance;
    
    // Check vertical adjacency (top-bottom)  
    const isVerticallyAdjacent = 
      Math.abs(rectA.y + rectA.height - rectB.y) <= tolerance || 
      Math.abs(rectB.y + rectB.height - rectA.y) <= tolerance;

    if (isHorizontallyAdjacent) {
      // Calculate vertical overlap
      const overlapTop = Math.max(rectA.y, rectB.y);
      const overlapBottom = Math.min(rectA.y + rectA.height, rectB.y + rectB.height);
      
      if (overlapBottom > overlapTop) {
        adjacency.isAdjacent = true;
        adjacency.side = rectA.x < rectB.x ? 'horizontal-lr' : 'horizontal-rl';
        adjacency.sharedArea = {
          start: overlapTop,
          end: overlapBottom,
          length: overlapBottom - overlapTop
        };
      }
    }

    if (isVerticallyAdjacent) {
      // Calculate horizontal overlap
      const overlapLeft = Math.max(rectA.x, rectB.x);
      const overlapRight = Math.min(rectA.x + rectA.width, rectB.x + rectB.width);
      
      if (overlapRight > overlapLeft) {
        adjacency.isAdjacent = true;
        adjacency.side = rectA.y < rectB.y ? 'vertical-tb' : 'vertical-bt';
        adjacency.sharedArea = {
          start: overlapLeft,
          end: overlapRight,
          length: overlapRight - overlapLeft
        };
      }
    }

    return adjacency;
  }, []);

  // Helper function to get line indices for each side based on rectangle's split state
  const getLineIndicesForSide = useCallback((rect, side) => {
    // Default split sides (assume false if not specified)
    const splitSides = {
      top: false,
      right: false,
      bottom: false,
      left: false,
      ...(rect.splitSides || {})
    };

    // Calculate line indices dynamically based on split state
    let currentIndex = 0;
    const sideIndices = {};

    // Top side
    if (splitSides.top) {
      sideIndices.top = [currentIndex, currentIndex + 1];
      currentIndex += 2;
    } else {
      sideIndices.top = [currentIndex];
      currentIndex += 1;
    }

    // Right side
    if (splitSides.right) {
      sideIndices.right = [currentIndex, currentIndex + 1];
      currentIndex += 2;
    } else {
      sideIndices.right = [currentIndex];
      currentIndex += 1;
    }

    // Bottom side
    if (splitSides.bottom) {
      sideIndices.bottom = [currentIndex, currentIndex + 1];
      currentIndex += 2;
    } else {
      sideIndices.bottom = [currentIndex];
      currentIndex += 1;
    }

    // Left side
    if (splitSides.left) {
      sideIndices.left = [currentIndex, currentIndex + 1];
      currentIndex += 2;
    } else {
      sideIndices.left = [currentIndex];
      currentIndex += 1;
    }

    return sideIndices[side] || [];
  }, []);

  // Check if rectangles have removed lines on the adjacent side
  const checkForRemovedLinesOnAdjacentSide = useCallback((rectA, rectB, adjacencyInfo) => {
    const removedLinesA = rectA.removedLines || [];
    const removedLinesB = rectB.removedLines || [];

    // Get dynamic line indices for each side based on split state
    let sideA, sideB;
    
    if (adjacencyInfo.side === 'horizontal-lr') {
      // A is left of B
      sideA = 'right';
      sideB = 'left';
    } else if (adjacencyInfo.side === 'horizontal-rl') {
      // A is right of B
      sideA = 'left';
      sideB = 'right';
    } else if (adjacencyInfo.side === 'vertical-tb') {
      // A is above B
      sideA = 'bottom';
      sideB = 'top';
    } else if (adjacencyInfo.side === 'vertical-bt') {
      // A is below B
      sideA = 'top';
      sideB = 'bottom';
    } else {
      return false;
    }

    const lineIndicesA = getLineIndicesForSide(rectA, sideA);
    const lineIndicesB = getLineIndicesForSide(rectB, sideB);

    // Check if any lines on the adjacent sides are removed
    const hasRemovedA = lineIndicesA.some(index => removedLinesA.includes(index));
    const hasRemovedB = lineIndicesB.some(index => removedLinesB.includes(index));

    return hasRemovedA || hasRemovedB;
  }, [getLineIndicesForSide]);

  // Find precise gaps based on removed lines
  const findPreciseGaps = useCallback((rectA, rectB, adjacencyInfo, tolerance) => {
    const gaps = [];
    const removedLinesA = rectA.removedLines || [];
    const removedLinesB = rectB.removedLines || [];

    // Calculate where gaps should be based on removed line segments
    if (adjacencyInfo.side.startsWith('horizontal')) {
      // Horizontal adjacency - gaps are vertical
      const leftRect = rectA.x < rectB.x ? rectA : rectB;
      const rightRect = rectA.x < rectB.x ? rectB : rectA;
      const leftRemovedLines = rectA.x < rectB.x ? removedLinesA : removedLinesB;
      const rightRemovedLines = rectA.x < rectB.x ? removedLinesB : removedLinesA;

      // Get dynamic line indices for each side
      const leftRightIndices = getLineIndicesForSide(leftRect, 'right');
      const rightLeftIndices = getLineIndicesForSide(rightRect, 'left');

      // Check each segment of the adjacent sides
      const maxSegments = Math.max(leftRightIndices.length, rightLeftIndices.length);
      
      for (let segmentIndex = 0; segmentIndex < maxSegments; segmentIndex++) {
        const leftLineIndex = leftRightIndices[segmentIndex];
        const rightLineIndex = rightLeftIndices[segmentIndex];
        
        const isLeftSegmentRemoved = leftLineIndex !== undefined && leftRemovedLines.includes(leftLineIndex);
        const isRightSegmentRemoved = rightLineIndex !== undefined && rightRemovedLines.includes(rightLineIndex);

        if (isLeftSegmentRemoved || isRightSegmentRemoved) {
          // Calculate the exact position of this segment gap
          const segmentHeight = leftRect.height / leftRightIndices.length;
          const segmentY = leftRect.y + (segmentIndex * segmentHeight);

          // Only create gap if it's within the shared area
          if (segmentY >= adjacencyInfo.sharedArea.start && 
              segmentY + segmentHeight <= adjacencyInfo.sharedArea.end) {
            gaps.push({
              start: { x: leftRect.x + leftRect.width, y: segmentY },
              end: { x: rightRect.x, y: segmentY + segmentHeight },
              orientation: 'vertical',
              segmentIndex
            });
          }
        }
      }

    } else if (adjacencyInfo.side.startsWith('vertical')) {
      // Vertical adjacency - gaps are horizontal
      const topRect = rectA.y < rectB.y ? rectA : rectB;
      const bottomRect = rectA.y < rectB.y ? rectB : rectA;
      const topRemovedLines = rectA.y < rectB.y ? removedLinesA : removedLinesB;
      const bottomRemovedLines = rectA.y < rectB.y ? removedLinesB : removedLinesA;

      // Get dynamic line indices for each side
      const topBottomIndices = getLineIndicesForSide(topRect, 'bottom');
      const bottomTopIndices = getLineIndicesForSide(bottomRect, 'top');

      // Check each segment of the adjacent sides
      const maxSegments = Math.max(topBottomIndices.length, bottomTopIndices.length);
      
      for (let segmentIndex = 0; segmentIndex < maxSegments; segmentIndex++) {
        const topLineIndex = topBottomIndices[segmentIndex];
        const bottomLineIndex = bottomTopIndices[segmentIndex];
        
        const isTopSegmentRemoved = topLineIndex !== undefined && topRemovedLines.includes(topLineIndex);
        const isBottomSegmentRemoved = bottomLineIndex !== undefined && bottomRemovedLines.includes(bottomLineIndex);

        if (isTopSegmentRemoved || isBottomSegmentRemoved) {
          // Calculate the exact position of this segment gap
          const segmentWidth = topRect.width / topBottomIndices.length;
          const segmentX = topRect.x + (segmentIndex * segmentWidth);

          // Only create gap if it's within the shared area
          if (segmentX >= adjacencyInfo.sharedArea.start && 
              segmentX + segmentWidth <= adjacencyInfo.sharedArea.end) {
            gaps.push({
              start: { x: segmentX, y: topRect.y + topRect.height },
              end: { x: segmentX + segmentWidth, y: bottomRect.y },
              orientation: 'horizontal',
              segmentIndex
            });
          }
        }
      }
    }

    return gaps;
  }, [getLineIndicesForSide]);

  // Function to create a gap-filling EditableLine
  const createGapFillerLine = useCallback((gap, rectAId, rectBId) => {
    const lineId = `gap-filler-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      id: lineId,
      type: 'line',
      x: gap.start.x,
      y: gap.start.y,
      nodes: [
        { x: 0, y: 0 }, // Start node
        { x: gap.end.x - gap.start.x, y: gap.end.y - gap.start.y } // End node
      ],
      isGapFiller: true,
      filledGapBetween: [rectAId, rectBId],
      orientation: gap.orientation,
      // Enhanced line properties for better EditableLine functionality
      alpha: 1,
      isPreview: false,
      strokeColor: 0x00aa00, // Green color
      strokeWidth: 2,
      showMeasurement: false // Prevent measurement text duplication
    };
  }, []);

  // Create EditableLines at snapped sides between adjacent rectangles
  const createSnappedSideLines = useCallback((shapes) => {
    // Safety check for input
    if (!shapes || !Array.isArray(shapes)) {
      console.error('❌ createSnappedSideLines received invalid shapes:', shapes);
      return [];
    }
    
    const rectangles = shapes.filter(shape => shape.type === 'rectangle');
    const existingLines = shapes.filter(shape => shape.type === 'line');
    const newSnappedLines = [];
    const tolerance = 5;
    const alignmentTolerance = 0.05; // 5% tolerance for considering rectangles "aligned"

    console.log('🔗 CREATE SNAPPED SIDE LINES: Starting', {
      rectangleCount: rectangles.length,
      existingLineCount: existingLines.length
    });

    // For each pair of rectangles, create EditableLines at snapped sides
    for (let i = 0; i < rectangles.length; i++) {
      for (let j = i + 1; j < rectangles.length; j++) {
        const rectA = rectangles[i];
        const rectB = rectangles[j];

        console.log('🔗 CHECKING PAIR:', {
          rectA: rectA.id,
          rectB: rectB.id
        });

        // Check if rectangles are adjacent
        const adjacencyInfo = getAdjacencyInfo(rectA, rectB, tolerance);
        if (!adjacencyInfo.isAdjacent) {
          console.log('❌ Not adjacent:', { rectA: rectA.id, rectB: rectB.id });
          continue;
        }

        console.log('✅ Rectangles are adjacent:', {
          rectA: rectA.id,
          rectB: rectB.id,
          side: adjacencyInfo.side,
          sharedArea: adjacencyInfo.sharedArea
        });

        // Create snapped side lines based on alignment and size
        const snappedLines = createSnappedSideLinesForPair(rectA, rectB, adjacencyInfo, tolerance, alignmentTolerance);
        
        // Check if these lines already exist and add new ones
        snappedLines.forEach(lineData => {
          // Generate a consistent ID for tracking deletion
          const lineTrackingId = `snapped-${rectA.id}-${rectB.id}-${lineData.lineType}`;
          
          // Check if this line was explicitly deleted
          if (deletedSnappedLines.has(lineTrackingId)) {
            console.log('🚫 Skipping recreating deleted snapped line:', lineTrackingId);
            return;
          }
          
          const existingLine = existingLines.find(line => 
            line.isSnappedSideLine && 
            Math.abs(line.x - lineData.x) < tolerance &&
            Math.abs(line.y - lineData.y) < tolerance &&
            line.nodes && line.nodes.length >= 2 &&
            Math.abs((line.x + line.nodes[1].x) - (lineData.x + lineData.nodes[1].x)) < tolerance &&
            Math.abs((line.y + line.nodes[1].y) - (lineData.y + lineData.nodes[1].y)) < tolerance
          );

          if (!existingLine) {
            // Add tracking ID to the line data
            lineData.trackingId = lineTrackingId;
            newSnappedLines.push(lineData);
            
            console.log('🔗 CREATING SNAPPED SIDE LINE:', {
              rectA: rectA.id,
              rectB: rectB.id,
              lineType: lineData.lineType,
              lineId: lineData.id,
              trackingId: lineTrackingId
            });
          }
        });

        // Also create gap-fillers for any removed lines
        const hasRemovedLinesOnAdjacentSide = checkForRemovedLinesOnAdjacentSide(rectA, rectB, adjacencyInfo);
        if (hasRemovedLinesOnAdjacentSide) {
          console.log('🎯 Found removed lines on adjacent side - creating gap fillers');
          const gaps = findPreciseGaps(rectA, rectB, adjacencyInfo, tolerance);
          
          gaps.forEach(gap => {
            const gapId = `gap-${rectA.id}-${rectB.id}-${gap.segmentIndex || 0}-${Math.round(gap.start.x)}-${Math.round(gap.start.y)}`;
            
            if (deletedGapFillers.has(gapId)) {
              console.log('🚫 Skipping recreating deleted gap-filler:', gapId);
              return;
            }
            
            const existingGapFiller = existingLines.find(line => 
              line.isGapFiller && 
              Math.abs(line.x - gap.start.x) < tolerance &&
              Math.abs(line.y - gap.start.y) < tolerance
            );

            if (!existingGapFiller) {
              const gapFiller = createGapFillerLine(gap, rectA.id, rectB.id);
              gapFiller.id = gapId;
              gapFiller.gapId = gapId;
              newSnappedLines.push(gapFiller);
            }
          });
        }
      }
    }

    console.log('🔗 CREATE SNAPPED SIDE LINES: Completed', {
      newSnappedLines: newSnappedLines.length,
      totalShapes: shapes.length + newSnappedLines.length
    });

    return [...shapes, ...newSnappedLines];
  }, [getAdjacencyInfo, checkForRemovedLinesOnAdjacentSide, findPreciseGaps, createGapFillerLine, deletedGapFillers, deletedSnappedLines]);

  // Create snapped side lines for a specific pair of rectangles
  const createSnappedSideLinesForPair = useCallback((rectA, rectB, adjacencyInfo, tolerance, alignmentTolerance) => {
    const lines = [];
    
    if (adjacencyInfo.side.startsWith('horizontal')) {
      // Horizontal snapping (left-right)
      const leftRect = rectA.x < rectB.x ? rectA : rectB;
      const rightRect = rectA.x < rectB.x ? rectB : rectA;
      
      // Check if rectangles are aligned (same Y position and height)
      const yAligned = Math.abs(leftRect.y - rightRect.y) <= tolerance;
      const heightMatch = Math.abs(leftRect.height - rightRect.height) <= (leftRect.height * alignmentTolerance);
      const isAligned = yAligned && heightMatch;
      
      console.log('🔗 Horizontal snapping analysis:', {
        leftRect: leftRect.id,
        rightRect: rightRect.id,
        yAligned,
        heightMatch,
        isAligned,
        strategy: isAligned ? '1 line' : '3 lines'
      });

      if (isAligned) {
        // Same alignment - create 1 line covering the entire shared boundary
        lines.push({
          id: `snapped-${leftRect.id}-${rightRect.id}-full-${Date.now()}`,
          type: 'line',
          x: leftRect.x + leftRect.width,
          y: adjacencyInfo.sharedArea.start,
          nodes: [
            { x: 0, y: 0 },
            { x: rightRect.x - (leftRect.x + leftRect.width), y: adjacencyInfo.sharedArea.end - adjacencyInfo.sharedArea.start }
          ],
          isSnappedSideLine: true,
          lineType: 'full',
          connectsBetween: [leftRect.id, rightRect.id],
          strokeColor: 0x0066cc,
          strokeWidth: 3, // Thicker line for unified room feeling
          showMeasurement: true,
          isUnifiedConnection: true, // Flag for unified room connection
          alpha: 1
        });
      } else {
        // Different alignment - create 3 lines
        const sharedStart = adjacencyInfo.sharedArea.start;
        const sharedEnd = adjacencyInfo.sharedArea.end;
        const leftTop = leftRect.y;
        const leftBottom = leftRect.y + leftRect.height;
        const rightTop = rightRect.y;
        const rightBottom = rightRect.y + rightRect.height;
        
        // Line 1: From top of smaller to top of larger (if needed)
        if (sharedStart > Math.min(leftTop, rightTop)) {
          const topStart = Math.min(leftTop, rightTop);
          lines.push({
            id: `snapped-${leftRect.id}-${rightRect.id}-top-${Date.now()}`,
            type: 'line',
            x: leftRect.x + leftRect.width,
            y: topStart,
            nodes: [
              { x: 0, y: 0 },
              { x: rightRect.x - (leftRect.x + leftRect.width), y: sharedStart - topStart }
            ],
            isSnappedSideLine: true,
            lineType: 'top-extension',
            connectsBetween: [leftRect.id, rightRect.id],
            strokeColor: 0x0066cc,
            strokeWidth: 2,
            showMeasurement: true
          });
        }
        
        // Line 2: The main shared boundary
        lines.push({
          id: `snapped-${leftRect.id}-${rightRect.id}-main-${Date.now()}`,
          type: 'line',
          x: leftRect.x + leftRect.width,
          y: sharedStart,
          nodes: [
            { x: 0, y: 0 },
            { x: rightRect.x - (leftRect.x + leftRect.width), y: sharedEnd - sharedStart }
          ],
          isSnappedSideLine: true,
          lineType: 'main',
          connectsBetween: [leftRect.id, rightRect.id],
          strokeColor: 0x0066cc,
          strokeWidth: 2,
          showMeasurement: true
        });
        
        // Line 3: From bottom of smaller to bottom of larger (if needed)
        if (sharedEnd < Math.max(leftBottom, rightBottom)) {
          const bottomEnd = Math.max(leftBottom, rightBottom);
          lines.push({
            id: `snapped-${leftRect.id}-${rightRect.id}-bottom-${Date.now()}`,
            type: 'line',
            x: leftRect.x + leftRect.width,
            y: sharedEnd,
            nodes: [
              { x: 0, y: 0 },
              { x: rightRect.x - (leftRect.x + leftRect.width), y: bottomEnd - sharedEnd }
            ],
            isSnappedSideLine: true,
            lineType: 'bottom-extension',
            connectsBetween: [leftRect.id, rightRect.id],
            strokeColor: 0x0066cc,
            strokeWidth: 2,
            showMeasurement: true
          });
        }
      }
    } else if (adjacencyInfo.side.startsWith('vertical')) {
      // Vertical snapping (top-bottom)
      const topRect = rectA.y < rectB.y ? rectA : rectB;
      const bottomRect = rectA.y < rectB.y ? rectB : rectA;
      
      // Check if rectangles are aligned (same X position and width)
      const xAligned = Math.abs(topRect.x - bottomRect.x) <= tolerance;
      const widthMatch = Math.abs(topRect.width - bottomRect.width) <= (topRect.width * alignmentTolerance);
      const isAligned = xAligned && widthMatch;
      
      console.log('🔗 Vertical snapping analysis:', {
        topRect: topRect.id,
        bottomRect: bottomRect.id,
        xAligned,
        widthMatch,
        isAligned,
        strategy: isAligned ? '1 line' : '3 lines'
      });

      if (isAligned) {
        // Same alignment - create 1 line covering the entire shared boundary
        lines.push({
          id: `snapped-${topRect.id}-${bottomRect.id}-full-${Date.now()}`,
          type: 'line',
          x: adjacencyInfo.sharedArea.start,
          y: topRect.y + topRect.height,
          nodes: [
            { x: 0, y: 0 },
            { x: adjacencyInfo.sharedArea.end - adjacencyInfo.sharedArea.start, y: bottomRect.y - (topRect.y + topRect.height) }
          ],
          isSnappedSideLine: true,
          lineType: 'full',
          connectsBetween: [topRect.id, bottomRect.id],
          strokeColor: 0x0066cc,
          strokeWidth: 3, // Thicker line for unified room feeling
          showMeasurement: true,
          isUnifiedConnection: true, // Flag for unified room connection
          alpha: 1
        });
      } else {
        // Different alignment - create 3 lines
        const sharedStart = adjacencyInfo.sharedArea.start;
        const sharedEnd = adjacencyInfo.sharedArea.end;
        const topLeft = topRect.x;
        const topRight = topRect.x + topRect.width;
        const bottomLeft = bottomRect.x;
        const bottomRight = bottomRect.x + bottomRect.width;
        
        // Line 1: From left of smaller to left of larger (if needed)
        if (sharedStart > Math.min(topLeft, bottomLeft)) {
          const leftStart = Math.min(topLeft, bottomLeft);
          lines.push({
            id: `snapped-${topRect.id}-${bottomRect.id}-left-${Date.now()}`,
            type: 'line',
            x: leftStart,
            y: topRect.y + topRect.height,
            nodes: [
              { x: 0, y: 0 },
              { x: sharedStart - leftStart, y: bottomRect.y - (topRect.y + topRect.height) }
            ],
            isSnappedSideLine: true,
            lineType: 'left-extension',
            connectsBetween: [topRect.id, bottomRect.id],
            strokeColor: 0x0066cc,
            strokeWidth: 2,
            showMeasurement: true
          });
        }
        
        // Line 2: The main shared boundary
        lines.push({
          id: `snapped-${topRect.id}-${bottomRect.id}-main-${Date.now()}`,
          type: 'line',
          x: sharedStart,
          y: topRect.y + topRect.height,
          nodes: [
            { x: 0, y: 0 },
            { x: sharedEnd - sharedStart, y: bottomRect.y - (topRect.y + topRect.height) }
          ],
          isSnappedSideLine: true,
          lineType: 'main',
          connectsBetween: [topRect.id, bottomRect.id],
          strokeColor: 0x0066cc,
          strokeWidth: 2,
          showMeasurement: true
        });
        
        // Line 3: From right of smaller to right of larger (if needed)
        if (sharedEnd < Math.max(topRight, bottomRight)) {
          const rightEnd = Math.max(topRight, bottomRight);
          lines.push({
            id: `snapped-${topRect.id}-${bottomRect.id}-right-${Date.now()}`,
            type: 'line',
            x: sharedEnd,
            y: topRect.y + topRect.height,
            nodes: [
              { x: 0, y: 0 },
              { x: rightEnd - sharedEnd, y: bottomRect.y - (topRect.y + topRect.height) }
            ],
            isSnappedSideLine: true,
            lineType: 'right-extension',
            connectsBetween: [topRect.id, bottomRect.id],
            strokeColor: 0x0066cc,
            strokeWidth: 2,
            showMeasurement: true
          });
        }
      }
    }
    
    return lines;
  }, []);

  // Calculate which lines to hide from rectangles when they have snapped side lines
  const calculateHiddenLinesForSnappedSides = useCallback((shapes) => {
    const rectangles = shapes.filter(shape => shape.type === 'rectangle');
    const snappedLines = shapes.filter(shape => shape.type === 'line' && shape.isSnappedSideLine);
    
    if (rectangles.length < 2) return {};

    console.log('📏 CALCULATE HIDDEN LINES FOR SNAPPED SIDES: Starting', {
      rectangleCount: rectangles.length,
      snappedLineCount: snappedLines.length
    });

    const hiddenLines = {};
    const tolerance = 5;

    // Initialize hidden lines map
    rectangles.forEach(rect => {
      hiddenLines[rect.id] = [];
    });

    // For each snapped side line, hide the corresponding rectangle lines
    snappedLines.forEach(snappedLine => {
      if (!snappedLine.connectsBetween || snappedLine.connectsBetween.length !== 2) return;
      
      const [rectAId, rectBId] = snappedLine.connectsBetween;
      const rectA = rectangles.find(r => r.id === rectAId);
      const rectB = rectangles.find(r => r.id === rectBId);
      
      if (!rectA || !rectB) return;

      // Get adjacency info to determine which lines to hide
      const adjacencyInfo = getAdjacencyInfo(rectA, rectB, tolerance);
      
      if (adjacencyInfo.isAdjacent) {
        console.log('🔗 Hiding rectangle lines for snapped side line:', {
          snappedLine: snappedLine.id,
          lineType: snappedLine.lineType,
          rectA: rectA.id,
          rectB: rectB.id,
          side: adjacencyInfo.side
        });
        
        // Hide the appropriate lines based on adjacency
        if (adjacencyInfo.side === 'horizontal-lr') {
          // A is left of B - hide right side of A and left side of B
          const lineIndicesA = getLineIndicesForSide(rectA, 'right');
          const lineIndicesB = getLineIndicesForSide(rectB, 'left');
          
          lineIndicesA.forEach(lineIndex => {
            if (!hiddenLines[rectA.id].includes(lineIndex)) {
              hiddenLines[rectA.id].push(lineIndex);
            }
          });
          lineIndicesB.forEach(lineIndex => {
            if (!hiddenLines[rectB.id].includes(lineIndex)) {
              hiddenLines[rectB.id].push(lineIndex);
            }
          });
        } else if (adjacencyInfo.side === 'horizontal-rl') {
          // A is right of B - hide left side of A and right side of B
          const lineIndicesA = getLineIndicesForSide(rectA, 'left');
          const lineIndicesB = getLineIndicesForSide(rectB, 'right');
          
          lineIndicesA.forEach(lineIndex => {
            if (!hiddenLines[rectA.id].includes(lineIndex)) {
              hiddenLines[rectA.id].push(lineIndex);
            }
          });
          lineIndicesB.forEach(lineIndex => {
            if (!hiddenLines[rectB.id].includes(lineIndex)) {
              hiddenLines[rectB.id].push(lineIndex);
            }
          });
        } else if (adjacencyInfo.side === 'vertical-tb') {
          // A is above B - hide bottom side of A and top side of B
          const lineIndicesA = getLineIndicesForSide(rectA, 'bottom');
          const lineIndicesB = getLineIndicesForSide(rectB, 'top');
          
          lineIndicesA.forEach(lineIndex => {
            if (!hiddenLines[rectA.id].includes(lineIndex)) {
              hiddenLines[rectA.id].push(lineIndex);
            }
          });
          lineIndicesB.forEach(lineIndex => {
            if (!hiddenLines[rectB.id].includes(lineIndex)) {
              hiddenLines[rectB.id].push(lineIndex);
            }
          });
        } else if (adjacencyInfo.side === 'vertical-bt') {
          // A is below B - hide top side of A and bottom side of B
          const lineIndicesA = getLineIndicesForSide(rectA, 'top');
          const lineIndicesB = getLineIndicesForSide(rectB, 'bottom');
          
          lineIndicesA.forEach(lineIndex => {
            if (!hiddenLines[rectA.id].includes(lineIndex)) {
              hiddenLines[rectA.id].push(lineIndex);
            }
          });
          lineIndicesB.forEach(lineIndex => {
            if (!hiddenLines[rectB.id].includes(lineIndex)) {
              hiddenLines[rectB.id].push(lineIndex);
            }
          });
        }
      }
    });

    console.log('📏 CALCULATE HIDDEN LINES FOR SNAPPED SIDES: Completed', {
      hiddenLines,
      totalHiddenLines: Object.values(hiddenLines).reduce((sum, arr) => sum + arr.length, 0)
    });

    return hiddenLines;
  }, [getAdjacencyInfo, getLineIndicesForSide]);

  // Update hidden lines when structure changes
  useEffect(() => {
    if (structure && structure.shapes) {
      const newHiddenLines = calculateHiddenLinesForSnappedSides(structure.shapes);
      
      // Update hidden lines map
      setHiddenLinesMap(newHiddenLines);
      
      console.log('📏 HIDDEN LINES CALCULATED FOR SNAPPED SIDES:', {
        structureId: structure.id,
        rectangleCount: structure.shapes.filter(s => s.type === 'rectangle').length,
        totalCalculated: Object.keys(newHiddenLines).length,
        hiddenLines: newHiddenLines
      });
    }
  }, [structure, calculateHiddenLinesForSnappedSides]);

  // Get hidden lines for each rectangle (lines that are replaced by connection lines)
  const getHiddenLinesForRect = useCallback((rectId) => {
    const hiddenLines = hiddenLinesMap[rectId] || [];
    
    console.log('📋 Getting hidden lines for rectangle:', {
      rectId,
      hiddenLines
    });
    
    return hiddenLines;
  }, [hiddenLinesMap]);

  // Manual trigger for connection line creation
  const triggerConnectionLineCreation = useCallback(() => {
    if (!structure || !structure.shapes) return;
    
    console.log('🔗 MANUAL CONNECTION LINE CREATION TRIGGERED');
    const currentShapes = structure.shapes;
    const shapesWithConnectionLines = createSnappedSideLines(currentShapes);
    
    // Safety checks for undefined arrays
    if (!shapesWithConnectionLines || !Array.isArray(shapesWithConnectionLines)) {
      console.error('❌ createSnappedSideLines returned invalid result:', shapesWithConnectionLines);
      return;
    }
    
    if (!currentShapes || !Array.isArray(currentShapes)) {
      console.error('❌ currentShapes is invalid:', currentShapes);
      return;
    }
    
    // Only update if we added new connection lines
    if (shapesWithConnectionLines.length > currentShapes.length) {
      console.log('🔗 MANUAL CONNECTION LINE CREATION: Adding connection lines', {
        structureId: structure.id,
        originalShapes: currentShapes.length,
        newShapes: shapesWithConnectionLines.length,
        connectionLinesAdded: shapesWithConnectionLines.length - currentShapes.length
      });
      
      onUpdate({
        ...structure,
        shapes: shapesWithConnectionLines
      });
    } else {
      console.log('🔗 MANUAL CONNECTION LINE CREATION: No connection lines needed');
    }
  }, [structure, createSnappedSideLines, onUpdate]);

  // Trigger connection line creation on initial structure creation
  useEffect(() => {
    if (structure && structure.shapes) {
      // Small delay to ensure other calculations are complete
      const timer = setTimeout(triggerConnectionLineCreation, 100);
      return () => clearTimeout(timer);
    }
  }, [structure?.id, triggerConnectionLineCreation]);   // Only trigger on structure ID change (new structure)

  // Listen for pattern updates to this merged structure
  useEffect(() => {
    const handlePatternUpdate = (event) => {
      const { structureId, pattern } = event.detail;
      if (structureId === structure.id) {
        console.log('🎨 MERGED STRUCTURE - Received pattern update:', pattern.name);
        setStructurePattern(pattern);
        
        // Apply the pattern only to the currently selected shape within the structure
        const updatedShapes = structure.shapes.map(shape => {
          if (selectedShapeId && shape.id === selectedShapeId) {
            // Only apply pattern to the selected shape
            return {
              ...shape,
              flooringPattern: pattern
            };
          }
          // Keep other shapes unchanged
          return shape;
        });
        
        onUpdate({
          ...structure,
          shapes: updatedShapes
        });
        
        console.log('✅ Applied pattern only to selected shape:', selectedShapeId);
      }
    };

    window.addEventListener('updateMergedStructurePattern', handlePatternUpdate);
    return () => {
      window.removeEventListener('updateMergedStructurePattern', handlePatternUpdate);
    };
  }, [structure, onUpdate, selectedShapeId]); // Added selectedShapeId to dependencies

  // Handle shape updates, including managing connection lines
  const handleShapeUpdate = useCallback((updatedShape) => {
    const updatedShapes = structure.shapes.map(shape => 
      shape.id === updatedShape.id ? {
        ...shape,
        ...updatedShape,
        // Explicitly preserve all curve-related data
        sides: updatedShape.sides || shape.sides,
        isCurveMode: updatedShape.isCurveMode !== undefined ? updatedShape.isCurveMode : shape.isCurveMode,
        controlPoint: updatedShape.controlPoint || shape.controlPoint,
        lines: updatedShape.lines || shape.lines,
        nodes: updatedShape.nodes || shape.nodes,
        // Preserve line deletion data
        removedLines: updatedShape.removedLines !== undefined ? updatedShape.removedLines : shape.removedLines,
        lineData: updatedShape.lineData || shape.lineData,
        // Preserve shared line hidden data
        hiddenBySharedLines: updatedShape.hiddenBySharedLines !== undefined ? updatedShape.hiddenBySharedLines : shape.hiddenBySharedLines
      } : shape
    );

    // Create connection lines and gap fillers
    const shapesWithConnectionLines = createSnappedSideLines(updatedShapes);

    // Safety check for undefined result
    if (!shapesWithConnectionLines || !Array.isArray(shapesWithConnectionLines)) {
      console.error('❌ createSnappedSideLines returned invalid result in handleShapeUpdate:', shapesWithConnectionLines);
      return;
    }

    onUpdate({
      ...structure,
      shapes: shapesWithConnectionLines
    });
  }, [structure, onUpdate, createSnappedSideLines]);

  // Handle when a shape inside the merged structure starts dragging
  const handleShapeDragStart = useCallback((shapeId, event) => {
    if (!isSelected) {
      onSelect();
    }

    setIsDragging(true);
    setDraggedShapeId(shapeId);
    setDragStart({
      x: event.data.global.x,
      y: event.data.global.y
    });
    setInitialStructure(JSON.parse(JSON.stringify(structure)));

    // Notify merge system
    window.dispatchEvent(new CustomEvent('shapeDragStart', {
      detail: { shapeId: structure.id }
    }));
  }, [structure, onSelect, getBoundingBox, isSelected]);

  // Handle merged structure dragging
  const handleMergedStructureDrag = useCallback((event) => {
    if (!isDragging || !dragStart || !initialStructure) return;

    const deltaX = event.data.global.x - dragStart.x;
    const deltaY = event.data.global.y - dragStart.y;

    // Update all shapes in the structure
    const updatedShapes = initialStructure.shapes.map(shape => ({
      ...shape,
      x: shape.x + deltaX,
      y: shape.y + deltaY
    }));

    const updatedStructure = {
      ...structure,
      shapes: updatedShapes
    };

    onUpdate(updatedStructure);

    // Calculate current bounding box for merge system
    const boundingBox = getBoundingBox(updatedShapes);
    
    // Notify merge system with current position
    window.dispatchEvent(new CustomEvent('shapeDragUpdate', {
      detail: { 
        shapeId: structure.id, 
        position: { x: boundingBox.x, y: boundingBox.y },
        boundingBox
      }
    }));
  }, [isDragging, dragStart, initialStructure, structure, onUpdate, getBoundingBox]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
    setInitialStructure(null);
    setDraggedShapeId(null);

    // Notify merge system
    window.dispatchEvent(new CustomEvent('shapeDragEnd', {
      detail: { shapeId: structure.id }
    }));
  }, [structure]);

  // Set up global event listeners for merged structure dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalPointerMove = (event) => {
      // Convert DOM event to PIXI-like event structure
      const stage = stageRef.current;
      if (!stage) return;

      const canvas = stage._canvas;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
      const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;

      const fakeEvent = {
        data: {
          global: {
            x: (event.clientX - rect.left) * scaleX,
            y: (event.clientY - rect.top) * scaleY
          }
        }
      };

      handleMergedStructureDrag(fakeEvent);
    };

    const handleGlobalPointerUp = () => {
      handleDragEnd();
    };

    document.addEventListener('pointermove', handleGlobalPointerMove);
    document.addEventListener('pointerup', handleGlobalPointerUp);
    document.addEventListener('mousemove', handleGlobalPointerMove);
    document.addEventListener('mouseup', handleGlobalPointerUp);

    return () => {
      document.removeEventListener('pointermove', handleGlobalPointerMove);
      document.removeEventListener('pointerup', handleGlobalPointerUp);
      document.removeEventListener('mousemove', handleGlobalPointerMove);
      document.removeEventListener('mouseup', handleGlobalPointerUp);
    };
  }, [isDragging, handleMergedStructureDrag, handleDragEnd, stageRef]);

  const handleShapeSelect = useCallback((selectedShape) => {
    // Track which individual shape is selected within the merged structure
    setSelectedShapeId(selectedShape.id);
    // Also select the whole structure
    onSelect();
  }, [onSelect]);

  // Reset selected shape when structure is deselected
  useEffect(() => {
    if (!isSelected) {
      setSelectedShapeId(null);
    }
  }, [isSelected]);

  // Custom shape drag handler that intercepts shape dragging
  const handleShapePointerDown = useCallback((shapeId, event) => {
    // If the shape is being dragged, convert it to merged structure drag
    handleShapeDragStart(shapeId, event);
    // Prevent the shape's own drag logic
    event.stopPropagation();
  }, [handleShapeDragStart]);

  const handleRightClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Show unmerge option
    const boundingBox = getBoundingBox(structure.shapes);
    window.dispatchEvent(new CustomEvent('showUnmergePopup', {
      detail: {
        structureId: structure.id,
        position: {
          x: event.clientX,
          y: event.clientY
        }
      }
    }));
  }, [structure, getBoundingBox]);

  if (!structure || !structure.shapes) return null;

  const boundingBox = getBoundingBox(structure.shapes);

  return (
    <Container
      ref={containerRef}
      interactive={false} // Remove interactivity from container
    >
      {/* Render all shapes in the structure with hidden line data */}
      {structure.shapes.map(shape => {
        // For rectangles, add hidden line information
        const shapeWithHiddenLines = shape.type === 'rectangle' ? {
          ...shape,
          hiddenBySharedLines: getHiddenLinesForRect(shape.id)
        } : shape;

        const shapeProps = {
          shape: shapeWithHiddenLines,
          isSelected: isSelected && selectedShapeId === shape.id,
          onUpdate: handleShapeUpdate,
          onSelect: () => handleShapeSelect(shape),
          stageRef,
          // Custom drag handler for merged structure dragging
          onShapePointerDown: (event) => handleShapePointerDown(shape.id, event),
          // Allow editable lines to work normally - no restrictions
          isPartOfMergedStructure: false, // Set to false to allow full interaction
          // Pass merged structure context
          isMergedStructureShape: true,
          mergedStructureId: structure.id,
          isBlueprintMode: isBlueprintMode
        };

        if (shape.type === 'rectangle') {
          return <Rectangle key={shape.id} {...shapeProps} selectedMeasurementUnit={selectedMeasurementUnit} />;
        } else if (shape.type === 'line') {
          // Handle snapped side lines and gap-filler lines
          if (shape.isSnappedSideLine) {
            // Snapped side lines - blue color, deletable
            const snappedLineProps = {
              ...shapeProps,
              shape: {
                ...shapeProps.shape,
                strokeColor: shape.isUnifiedConnection ? 0x0077dd : 0x0066cc, // Brighter blue for unified connections
                strokeWidth: shape.isUnifiedConnection ? 3 : 2, // Thicker for unified connections
                showMeasurement: true,
                isSnappedSideLine: true,
                alpha: shape.isUnifiedConnection ? 1 : 0.9, // More prominent for unified connections
                // Enhanced measurement style for unified connections
                measurementStyle: shape.isUnifiedConnection ? {
                  fontSize: 14,
                  fontWeight: 'bold',
                  fill: 0x0077dd,
                  stroke: 0xffffff,
                  strokeThickness: 2
                } : undefined
              },
              // Enable deletion for snapped side lines
              isSharedLine: true,
              onDelete: () => {
                console.log('🗑️ Deleting snapped side line:', shape.id, 'Type:', shape.lineType);
                
                // Track this snapped side line as deleted to prevent recreation
                if (shape.trackingId) {
                  setDeletedSnappedLines(prev => {
                    const newSet = new Set([...prev, shape.trackingId]);
                    console.log('📝 Marking snapped side line as deleted:', shape.trackingId);
                    return newSet;
                  });
                }
                
                // Remove the snapped side line from the structure
                const updatedShapes = structure.shapes.filter(s => s.id !== shape.id);
                onUpdate({
                  ...structure,
                  shapes: updatedShapes
                });
              }
            };
            
            return <EditableLine key={shape.id} {...snappedLineProps} selectedMeasurementUnit={selectedMeasurementUnit} />;
          } else if (shape.isGapFiller) {
            // Gap-filler lines - green color, special deletion handling
            const gapFillerLineProps = {
              ...shapeProps,
              shape: {
                ...shapeProps.shape,
                strokeColor: 0x00aa00, // Green color for gap fillers
                strokeWidth: 2,
                showMeasurement: false, // Hide measurement text to prevent duplication
                isGapFiller: true
              },
              // Enable deletion for gap-filler lines
              isSharedLine: true,
              onDelete: () => {
                console.log('🗑️ Deleting gap-filler line:', shape.id);
                
                // Track this gap-filler as deleted to prevent recreation
                if (shape.gapId) {
                  setDeletedGapFillers(prev => {
                    const newSet = new Set([...prev, shape.gapId]);
                    console.log('📝 Marking gap-filler as deleted:', shape.gapId);
                    return newSet;
                  });
                }
                
                // Remove the gap-filler line from the structure
                const updatedShapes = structure.shapes.filter(s => s.id !== shape.id);
                onUpdate({
                  ...structure,
                  shapes: updatedShapes
                });
              }
            };
            
            return <EditableLine key={shape.id} {...gapFillerLineProps} selectedMeasurementUnit={selectedMeasurementUnit} />;
          } else {
            // Regular EditableLine - default behavior
          return <EditableLine key={shape.id} {...shapeProps} selectedMeasurementUnit={selectedMeasurementUnit} />;
          }
        }
        
        return null;
      })}

      {/* Only show merge indicator badge - positioned away from interactive elements */}
      {isSelected && (
        <Graphics
          x={boundingBox.x + boundingBox.width + 20} // Moved further away
          y={boundingBox.y - 20}
          interactive={false} // Not interactive
          draw={(g) => {
            g.clear();
            
            // Merge indicator badge
            g.lineStyle(2, 0x4a90e2, 1);
            g.beginFill(0x4a90e2, 0.9);
            g.drawCircle(0, 0, 12);
            g.endFill();
            
            // "M" for merged - larger and clearer
            g.beginFill(0xffffff);
            g.drawRect(-6, -8, 2, 10);
            g.drawRect(-2, -8, 2, 10);
            g.drawRect(2, -8, 2, 10);
            g.drawRect(6, -8, 2, 10);
            g.drawRect(-4, -4, 2, 2);
            g.drawRect(0, -4, 2, 2);
            g.drawRect(4, -4, 2, 2);
            g.endFill();
            
            // Badge label
            g.beginFill(0x4a90e2, 0.8);
            g.drawRect(-20, 16, 40, 14);
            g.endFill();

            // Show hidden lines count if any
            const totalHiddenLines = Object.values(hiddenLinesMap).reduce((sum, arr) => sum + arr.length, 0);
            if (totalHiddenLines > 0) {
              g.beginFill(0x00aa00, 0.9);
              g.drawCircle(15, -5, 8);
              g.endFill();
              
              // Indicate lines are hidden
              g.beginFill(0xffffff);
              g.drawRect(11, -9, 8, 8);
              g.endFill();
            }
          }}
        />
      )}
    </Container>
  );
};

export default MergedStructure; 